// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getReviewAndPayCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getReviewAndPayPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/LoadDataIfNeeded";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import AddressErrorModal from "@insite/content-library/Components/AddressErrorModal";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import StepperContextProvider from "@plumberssupply/App/Widgets/CheckoutStepper/StepperContext";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends PageProps { }

const mapDispatchToProps = {
    loadDataIfNeeded,
};

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = getReviewAndPayCartState(state);
    const promotionsDataView = getReviewAndPayPromotions(state);

    const isLoadDataNeeded =
        ((!cartState.value || (cartState.value.lineCount && !cartState.value.cartLines)) &&
            !cartState.isLoading) || (!promotionsDataView.value && !promotionsDataView.isLoading);

    const { isPlacingOrder, isCheckingOutWithPayPay } = state.pages.checkoutReviewAndSubmit;

    return {
        cartId,
        isLoadDataNeeded: isLoadDataNeeded || !getCurrentCountries(state),
        isLoading: isPlacingOrder || isCheckingOutWithPayPay,
        cartLinesLength: cartState.value?.cartLines?.length || 0,
    };
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;
const ExpressCheckoutPage = ({
    id,
    cartId,
    isLoadDataNeeded,
    isLoading,
    loadDataIfNeeded,
    cartLinesLength,
}: Props) => {

    useEffect(() => {
        if (isLoadDataNeeded) {
            loadDataIfNeeded({ cartId, cartLinesShouldBeExpanded: true });
        }
    });

    const hideZone = isLoading && !cartLinesLength;
    return (
        <StepperContextProvider initialStep={3}>
            <Page data-test-selector="expressCheckoutPage" >
                <LoadingOverlay
                    style={{ width: "100%", minHeight: "80vh" }}
                    loading={isLoading}
                >
                    {!hideZone && <Zone zoneName="Content" contentId={id} />}
                    <AddressErrorModal />
                </LoadingOverlay>
            </Page>
        </StepperContextProvider>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ExpressCheckoutPage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;